import { useEffect, useState } from "react";
import { addressType } from "../../utils/addressFormatter";
import { formatCurrency } from "../../utils/formatter/currency";
import fetchDeliveryCosts from "~/utils/fetchDeliveryCosts";
import { useShoppingCart } from "~/utils/context/useShoppingCart";
import { countries } from '~/utils/countries';
import InputError from "../Form/InputError";
import Input from "../Form/Input";
import { ShippingItem } from "~/utils/types/shoppingCart";
import { isCountryBlocked, getBlockedCountryMessage } from "~/utils/blockedCountries";
import { getCountryRequirements, hasAdditionalRequirements } from "~/utils/shippingRequirements";
import validateAddress from "~/utils/validateAddress";
import { isFetchWrapperError } from "~/utils/errorCheckers";

export default function ShippingInformation({ parcelError, setSubButtonMessage, order, countryAbbr }: { parcelError: { message: string } | null, setSubButtonMessage: React.Dispatch<React.SetStateAction<string>>, order?: any, countryAbbr: string }) {

    const [address, setAddress] = useState<addressType>(null);
    const [deliveryMethod, setDeliveryMethod] = useState<any | null>(null);
    const [placesError, setPlacesError] = useState<string>('');
    const [hasAddressError, setHasAddressError] = useState<boolean>(false);
    const [addressError, setAddressError] = useState<string>('');
    const [additionalFields, setAdditionalFields] = useState<{ [key: string]: string }>({});
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

    const { updateShippingCosts, fieldHasError, getFieldError, shippingHasError, checkIfCartHasSupporterPack, getShippingCosts, memoizeShippingCosts, cardTotal } = useShoppingCart();

    useEffect(() => {
        async function updateShippingCostsAfterAddressSelect() {
            if (!address || !address.countryAbbr || !address.postalCode) return;
            setPlacesError('');
            setFieldErrors({});
            setHasAddressError(false);
            if (address.city !== '') {
                // Check if shipping to this country is blocked
                if (isCountryBlocked(address.countryAbbr)) {
                    setPlacesError(getBlockedCountryMessage(address.country));
                    setHasAddressError(true);
                    return;
                }

                // Reset additional fields when country changes
                setAdditionalFields({});
            }
            let deliveryCosts = await fetchDeliveryCosts(address);
            if (!deliveryCosts) {
                setHasAddressError(true);
            } else {
                setHasAddressError(false);
            }

            const cartHasSupporterPack = checkIfCartHasSupporterPack();
            let shippingItem: ShippingItem = {
                id: 'method_' + deliveryCosts.method.id,
                price: deliveryCosts.method.price,
                type: 'shipping',
                quantity: 1,
                code: deliveryCosts.method.id,
                originalPrice: deliveryCosts.originalPriceWithoutTaxes
            }
            let shippingTitle = deliveryCosts.method.name;
            if (cartHasSupporterPack) {
                memoizeShippingCosts(shippingItem.price);
                shippingItem.price = 0;
                shippingTitle = 'Supporter Pack Free Delivery';
            }
            setDeliveryMethod(shippingItem);
            updateShippingCosts(shippingItem, shippingTitle);
        }
        updateShippingCostsAfterAddressSelect();
    }, [address])

    useEffect(() => {
        if (deliveryMethod === null) {
            if (cardTotal > 0) {
                setSubButtonMessage('Please fill in your shipping details and calculate your shipping costs.');
            } else {
                setSubButtonMessage('Select two or more cards to proceed.');
            }
        } else {
            if (cardTotal > 0) {
                setSubButtonMessage('Payment fees will be calculated at checkout.');
            } else {
                setSubButtonMessage('Select two or more cards to proceed.');
            }
        }
    }, [deliveryMethod])

    useEffect(() => {
        if (parcelError && parcelError.message) {
            setHasAddressError(true);
            let errors = parcelError.message.split(', ');
            for (let error of errors) {
                if (error.includes('name')) {
                    setFieldErrors(prev => ({ ...prev, ['firstName']: error.split(':')[1].replace('\"', '').replace('\"', '') }));
                    setFieldErrors(prev => ({ ...prev, ['lastName']: error.split(':')[1].replace('\"', '').replace('\"', '') }));
                }
                setFieldErrors(prev => ({ ...prev, [error.split(':')[0]]: error.split(':')[1].replace('\"', '').replace('\"', '') }));
            }
            // setParcelError({ message: errors[0] });
            console.log(errors);
        }
    }, [parcelError])

    useEffect(() => {
        setAddress({
            city: order?.city || '',
            country: order?.country || '',
            postalCode: order?.postalCode || '',
            state: order?.state || '',
            stateAbbr: order?.stateAbbr || '',
            street: order?.address || '',
            countryAbbr: order?.countryAbbr || countryAbbr || '',
            address_2: order?.address2 || ''
        })
    }, [])

    async function handleAddressBlur() {
        const form = document.getElementById('orderForm') as HTMLFormElement;
        const formData: FormData = new FormData(form || undefined);

        if (!formData) {
            return null;
        }

        let address = {
            address: formData.get('address') || '',
            city: formData.get('city') || '',
            state: formData.get('state') || '',
            country: formData.get('country') || '',
            postalCode: formData.get('postalCode') || '',
            address2: formData.get('address_2') || '',
        }

        if (!address.address || !address.city || !address.state || !address.country || !address.postalCode) {
            setFieldErrors({
                address: !address.address ? 'Please enter your street address' : '',
                city: !address.city ? 'Please enter your city' : '',
                state: !address.state ? 'Please enter your state' : '',
                country: !address.country ? 'Please enter your country' : '',
                postalCode: !address.postalCode ? 'Please enter your postal code' : ''
            })
            return;
        }

        const validatedAddress = await validateAddress(address);

        if (isFetchWrapperError(validatedAddress)) {
            setHasAddressError(true);
            //@ts-ignore
            setAddressError(validatedAddress.description)
            return;
        }

        setAddress({
            city: validatedAddress.city,
            country: address.country.toString(),
            postalCode: validatedAddress.postalCode,
            state: validatedAddress.state,
            stateAbbr: validatedAddress.stateAbbr,
            street: validatedAddress.street,
            countryAbbr: validatedAddress.country
        })
    }

    function setCountry(country: string) {
        setHasAddressError(false);
        setAddress({
            city: '',
            country: country,
            postalCode: '',
            state: '',
            stateAbbr: '',
            street: '',
            countryAbbr: country
        })
    }

    return (
        <div className="border-gray-200 ">
            <h2 className="text-lg font-medium text-gray-900">Shipping information</h2>

            <div className="grid grid-cols-1 mt-4 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                <div>
                    <Input fieldErrors={fieldErrors} type="text" name="firstName" id="firstName" autoComplete="first-name" readOnly={false} label="First name" hasTab={true} defaultValue={order?.firstName} />
                </div>
                <div>
                    <Input fieldErrors={fieldErrors} type="text" name="lastName" id="lastName" autoComplete="family-name" readOnly={false} label="Last name" hasTab={true} defaultValue={order?.lastName} />
                </div>

                <div className="my-1 border-t border-gray-200 sm:col-span-2"></div>

                <>
                    {placesError !== '' ?
                        <div className="p-4 rounded-md bg-red-50 sm:col-span-2">
                            <div className="flex">
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">Uh-oh!</h3>
                                    <div className="mt-2 text-sm text-red-700">
                                        <p>Unfortunately, we cannot send our cards to this country. If possible, please select an other address to deliver to.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        :
                        <>
                            {hasAddressError &&
                                <div className="w-full col-span-2 p-4 text-center text-red-700 bg-red-100">
                                    Please be sure to check your address. We couldn't find an option to ship our products to you.
                                    <br />
                                    <small>{addressError}</small>
                                </div>
                            }
                            <div className="sm:col-span-2">
                                <Input onBlur={() => { }} fieldErrors={fieldErrors} type="text" name="address" id="address" readOnly={false} label="Street and house number (max 35 characters)" autoComplete={''} defaultValue={address?.street} maxLength={35} hasTab={true} required={true} />
                            </div>
                            <div className="sm:col-span-2">
                                <Input onBlur={() => { }} type="text" maxLength={35} fieldErrors={fieldErrors} name="address_2" id="address_2" readOnly={placesError !== ''} label="Apartment, suite, etc. (max 35 characters)" autoComplete={''} defaultValue={address?.address_2} hasTab={true} />
                            </div>
                            <div>
                                <Input onBlur={() => { }} fieldErrors={fieldErrors} type="text" name="city" id="city" readOnly={false} label="City" autoComplete={''} defaultValue={address?.city} hasTab={true} />
                            </div>

                            <div className="relative">
                                <Input onBlur={() => { }} fieldErrors={fieldErrors} type="text" name="country" id="country" readOnly={false} label="Country" autoComplete={''} defaultValue={address?.country} hasTab={true} />
                            </div>

                            <div>
                                <Input onBlur={() => { }} fieldErrors={fieldErrors} type="text" name="state" id="state" readOnly={false} label="State / Province" autoComplete={''} defaultValue={address?.state} hasTab={true} />
                            </div>
                            <div>
                                <Input onBlur={() => { }} fieldErrors={fieldErrors} type="text" name="postalCode" id="postalCode" readOnly={false} label="Postal code" autoComplete={''} defaultValue={address?.postalCode} hasTab={true} />
                            </div>
                            <div className="absolute -left-[110vw]">
                                <Input fieldErrors={fieldErrors} type="text" name="countryAbbr" id="countryAbbr" readOnly={true} label="" autoComplete={''} defaultValue={address?.countryAbbr} hasTab={false} />
                                <Input fieldErrors={fieldErrors} type="text" name="stateAbbr" id="stateAbbr" readOnly={true} label="" autoComplete={''} defaultValue={address?.stateAbbr} hasTab={false} />
                            </div>
                        </>
                    }

                    {address && hasAdditionalRequirements(address.countryAbbr) && (
                        <div className="sm:col-span-2">
                            <div className="mb-4 p-4 bg-gray-50 rounded-md">
                                <h3 className="text-sm font-medium text-gray-900">Additional Information Required</h3>
                                <p className="mt-1 text-sm text-gray-500">{getCountryRequirements(address.countryAbbr)?.info}</p>
                            </div>
                            {getCountryRequirements(address.countryAbbr)?.additionalFields.map((field) => (
                                <div key={field.name} className="mt-4">
                                    <Input
                                        type={field.type}
                                        name={field.name}
                                        id={field.name}
                                        label={field.label}
                                        readOnly={false}
                                        defaultValue={additionalFields[field.name] || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setAdditionalFields(prev => ({ ...prev, [field.name]: value }));

                                            // Validate if there's a validation pattern
                                            if (field.validation && !field.validation.test(value)) {
                                                setFieldErrors(prev => ({
                                                    ...prev,
                                                    [field.name]: field.errorMessage || 'Invalid format'
                                                }));
                                            } else {
                                                setFieldErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors[field.name];
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        hasTab={true}
                                    />
                                    {fieldErrors[field.name] && (
                                        <p className="mt-2 text-sm text-red-600">{fieldErrors[field.name]}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="my-1 border-t border-gray-200 sm:col-span-2"></div>
                </>

                <h2 className="text-lg font-medium text-gray-900">Contact information</h2>

                <div className="sm:col-span-2">
                    <Input type="email" name="email" id="email" autoComplete="email" label="Email address" readOnly={false} hasTab={true} defaultValue={order?.email} />
                </div>

                <div className="sm:col-span-2">
                    <label htmlFor="phone" className={`block text-sm font-medium ${fieldHasError('phone') ? 'text-red-700' : 'text-gray-700'} `}>
                        Phone Number
                    </label>
                    <div className="relative mt-2 rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 flex items-center">
                            <label htmlFor="country" className="sr-only">
                                Country
                            </label>
                            <select
                                id="countryPrefix"
                                name="countryPrefix"
                                autoComplete="country"
                                className="h-full py-0 pl-3 pr-3 text-gray-500 bg-transparent border-r-0 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                defaultValue={countryAbbr}
                                onChange={(e) => { setCountry(e.target.value) }}
                            >
                                {countries.map(country => {
                                    return (
                                        <option key={`countrySelect${country.name}`} value={country.code}>{country.code} ({country.dialling_code})</option>
                                    )
                                })}
                            </select>
                        </div>
                        <input
                            id="phoneNumber"
                            name="phoneNumber"
                            type="number"
                            className={`pl-[6.5rem] block w-full rounded-md border-gray-300 shadow-sm focus:outline-none  focus:border-gray-300 focus:ring-gray-200 sm:text-sm read-only:cursor-not-allowed read-only:bg-gray-100 read-only:text-gray-500 read-only:ring-gray-200 ${fieldHasError('firstName') ? 'text-red-900 ring-red-300 ring-1 ring-inset placeholder:text-red-300' : ''}`}
                            readOnly={placesError !== ''}
                            defaultValue={order?.phoneNumber}
                        />
                    </div>
                    {fieldHasError('phoneNumber') &&
                        <InputError message={getFieldError('phoneNumber')} />
                    }
                </div>
            </div>

            {deliveryMethod ?

                <div className="pt-10 mt-10 border-t border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">Delivery costs</h2>

                    <fieldset aria-label="Delivery method" className="mt-4">
                        <div className="flex items-center justify-between">
                            <dt className={`text-sm ${shippingHasError ? 'text-red-600 font-bold' : ''}`}>Shipping
                                {shippingHasError &&
                                    <InputError message="Please select a shipping method" />
                                }
                            </dt>
                            <dd className="text-sm font-medium text-gray-900">{formatCurrency('USD', getShippingCosts())}</dd>
                        </div>

                    </fieldset>
                </div>
                :
                <div className="pt-10 text-center">
                    <button className="flex gap-2 items-center justify-center mx-auto rounded-md border border-transparent bg-black disabled:bg-opacity-20 disabled:hover:bg-black disabled:hover:bg-opacity-20 px-4 lg:px-12 py-3 text-base font-medium text-white shadow-sm hover:bg-blackHover focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 focus:ring-offset-gray-50" onClick={(e) => { e.preventDefault(); setDeliveryMethod(null); handleAddressBlur() }}>Calculate shipping costs</button>
                </div>
            }
        </div >
    )
};
